import { CaretSortIcon, CheckIcon, UpdateIcon } from '@radix-ui/react-icons';
import { useEffect, useState, } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "../ui/command";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription, DialogHeader,
    DialogTitle,
    DialogTrigger
} from "../ui/dialog";
import { DropdownMenuItem } from "../ui/dropdown-menu";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../ui/form';
import { Label } from '../ui/label';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../ui/popover";

import { useForm } from 'react-hook-form';
import regions from '../../enums/regions.json';
import sport_classes from '../../enums/sport_classes.json';
import api from '../../lib/api';
import { DatePickerSingle } from '../datePickerSingle';
import { Checkbox } from '../ui/checkbox';
import { toast } from '../ui/use-toast';

const AddCertificationDialog = ({ fetchQualifications, bunny, competitionClass, dropdown, reloadBunnySinglePage }) => {
    const [isLoading, setIsLoading] = useState(false);
    const form = useForm();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [sportClassOpen, setSportClassOpen] = useState(false);
    const [regionOpen, setRegionOpen] = useState(false);

    const [sportClassId, setSportClassId] = useState(competitionClass?.sport_class_id || '');

    useEffect(() => {
        if (competitionClass?.sport_class_id) {
            setSportClassId(competitionClass.sport_class_id);
        }
    }, [competitionClass]);

    // Helper function to send registration id to certificate database
    const getRegistrationId = () => {
        if (!competitionClass || !bunny) {
            // console.error("Both competitionClass and bunny must be provided.");
            return null; // Return null if either is missing
        }

        // Find the registration that matches the bunny's id
        const registration = competitionClass.registrations.find(
            (reg) => reg.bunny.id === bunny.id
        );

        if (registration) {
            return registration.registrationId; // Return the registration ID if found
        } else {
            console.warn("No registration found for the given bunny.");
            return null; // Return null if no match is found
        }
    };

    const convertToUTC = (date) => {
        // If the input is not a Date, convert it to a Date object
        const validDate = date instanceof Date ? date : new Date(date);

        // Check if the Date is valid
        if (isNaN(validDate)) {
            console.error("Invalid date provided:", date);
            return '';  // Return an empty string or handle it however you prefer
        }

        // Adjust to UTC and format as 'YYYY-MM-DD'
        return new Date(validDate.getTime() - validDate.getTimezoneOffset() * 60000)
            .toISOString()
            .split('T')[0];
    };

    const [formData, setFormData] = useState({
        sportClassId: competitionClass ? competitionClass.sport_class_id : '',
        region: '',
        free: false,
        date: competitionClass ? convertToUTC(competitionClass.date) : '',
    });

    const handleChange = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const registrationId = getRegistrationId();

            const payload = {
                bunnyId: bunny.id,
                ...formData,
            };

            if (registrationId) {
                payload.registrationId = registrationId; // Add only if available
            }

            const response = await api.post(`https://skhrf-api.cmdigital.se/v1/bunnies/certificate`, payload);

            if (fetchQualifications) {
                fetchQualifications();
            } else if (reloadBunnySinglePage) {
                reloadBunnySinglePage();
            }

            toast({
                title: 'Certifikat tillagt',
                description: `Certifikat från ${formData.date} och ${formData.region} blev tillagt i ${getSportClassNameById(formData.sportClassId)}`,  // Using full class name
            });
            form.reset();  // Reset react-hook-form fields
            setFormData({
                sportClassId: competitionClass ? competitionClass.sport_class_id : '',
                region: '',
                free: false,
                date: competitionClass ? convertToUTC(competitionClass.date) : '',
            });
            setIsLoading(false);
            setIsDialogOpen(false);
        } catch (error) {
            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: error?.response?.data?.error,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const getSportClassNameById = (id) => {
        const sportClass = sport_classes.find((sport_class) => sport_class.id === id);
        return sportClass ? sportClass.sport_class_name : "Okänd klass"; // Fallback if ID is not found
    };

    const handleDateChange = (date) => {
        const formattedDate = convertToUTC(date)

        setFormData(prevState => ({
            ...prevState,
            date: formattedDate,
        }));
    };

    useEffect(() => {
        if (competitionClass) {
            form.setValue('sportClassId', competitionClass.sport_class_id);
            form.setValue('date', convertToUTC(competitionClass.date));
        }
    }, [competitionClass]);

    useEffect(() => {
        if (!isDialogOpen) {
            // Clear form data when the dialog is closed
            form.reset();
            setFormData({
                sportClassId: competitionClass ? competitionClass.sport_class_id : '',
                region: '',
                free: false,
                date: competitionClass ? convertToUTC(competitionClass.date) : '',
            });
        }
    }, [isDialogOpen, competitionClass]);

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            {dropdown ? (
                <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
                    <DialogTrigger className='flex items-center w-full'>
                        Lägg till certifikat
                    </DialogTrigger>
                </DropdownMenuItem>
            ) : (
                <DialogTrigger className='flex items-center w-full' asChild>
                    <Button>
                        Lägg till certifikat
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent className="flex flex-col gap-6">
                <DialogHeader className='text-left'>
                    <DialogTitle>
                        Lägg till certifikat
                    </DialogTitle>
                    <DialogDescription>
                        Ange information för att lägga till certifikat.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={handleChange} className="flex flex-col gap-6">
                        <FormField
                            control={form.control}
                            name="sportClassId"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel htmlFor='competitionClass'>Klass</FormLabel>
                                    <FormControl>
                                        <Popover modal={true} open={sportClassOpen} onOpenChange={setSportClassOpen}>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    id='competitionClass'
                                                    disabled={!!competitionClass}
                                                    variant="outline"
                                                    role="combobox"
                                                    className={cn(
                                                        "w-full justify-between",
                                                        !field.value && "text-muted-foreground"
                                                    )}
                                                >
                                                    {sportClassId
                                                        ? sport_classes.find(sc => sc.id === sportClassId)?.sport_class_name
                                                        : "Välj klass"}
                                                    <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="popover-content-width-full p-0">
                                                <Command>
                                                    <CommandInput placeholder="Sök klass..." />
                                                    <CommandList>
                                                        <CommandEmpty>Ingen klass hittades.</CommandEmpty>
                                                        <CommandGroup>
                                                            {sport_classes
                                                                .filter((sport_class) => [17, 14, 11, 5].includes(sport_class.id)) // Filter for only Elit classes
                                                                .map((sport_class) => (
                                                                    <CommandItem
                                                                        className='cursor-pointer'
                                                                        value={sport_class.id}
                                                                        key={sport_class.id}
                                                                        onSelect={() => {
                                                                            setSportClassOpen(false);
                                                                            form.setValue("sportClassId", sport_class.id);
                                                                            setFormData(prevState => ({
                                                                                ...prevState,
                                                                                sportClassId: sport_class.id, // Update sportClassId in formData
                                                                            }));
                                                                        }}
                                                                    >
                                                                        <CheckIcon
                                                                            className={cn(
                                                                                "mr-2 h-4 w-4",
                                                                                sport_class.id === field.value
                                                                                    ? "opacity-100"
                                                                                    : "opacity-0"
                                                                            )}
                                                                        />
                                                                        {sport_class?.sport_class_name}
                                                                    </CommandItem>
                                                                ))}
                                                        </CommandGroup>

                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="date"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel htmlFor='date'>Datum för certifikat</FormLabel>
                                    <FormControl>
                                        <DatePickerSingle
                                            id='date'
                                            onDateChange={handleDateChange}
                                            selectedDate={competitionClass ? competitionClass.date : formData.date}
                                            disabled={!!competitionClass}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="region"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel htmlFor='region'>Region</FormLabel>
                                    <FormControl>
                                        <Popover modal={true} open={regionOpen} onOpenChange={setRegionOpen}>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    id='region'
                                                    variant="outline"
                                                    role="combobox"
                                                    className={cn(
                                                        "w-full justify-between",
                                                        !field.value && "text-muted-foreground"
                                                    )}
                                                >
                                                    {field.value
                                                        ? regions.find(
                                                            (region) => region.region_name === field.value
                                                        )?.region_name
                                                        : "Välj region"}
                                                    <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="popover-content-width-full p-0">
                                                <Command>
                                                    <CommandInput placeholder="Sök region..." />
                                                    <CommandList>
                                                        <CommandEmpty>Ingen region hittades.</CommandEmpty>
                                                        <CommandGroup>
                                                            {regions.map((region) => (
                                                                <CommandItem
                                                                    className='cursor-pointer'
                                                                    value={region.region_name}
                                                                    key={region.region_name}
                                                                    onSelect={() => {
                                                                        setRegionOpen(false);
                                                                        form.setValue("region", region.region_name);
                                                                        setFormData(prevState => ({
                                                                            ...prevState,
                                                                            region: region.region_name, // Update sportClassId in formData
                                                                        }));
                                                                    }}
                                                                >
                                                                    <CheckIcon
                                                                        className={cn(
                                                                            "mr-2 h-4 w-4",
                                                                            region.region_name === field.value
                                                                                ? "opacity-100"
                                                                                : "opacity-0"
                                                                        )}
                                                                    />
                                                                    {region.region_name}
                                                                </CommandItem>
                                                            ))}
                                                        </CommandGroup>
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="free"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel htmlFor='free'>Fricertifikat</FormLabel>
                                    <FormControl>
                                        <Label className='font-normal flex align-center'>
                                            <Checkbox
                                                id='free'
                                                className='mr-2'
                                                checked={field.value}  // Controlled by form's state
                                                onCheckedChange={(checked) => {
                                                    field.onChange(checked);  // Update react-hook-form
                                                    setFormData(prevState => ({
                                                        ...prevState,
                                                        free: checked,  // Update formData state
                                                    }));
                                                }} />
                                            Bocka för rutan om certifikatet är ett fricert.
                                        </Label>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className='grid grid-cols-2 gap-3'>
                            <DialogClose asChild>
                                <Button type='button' variant='secondary'>Avbryt</Button>
                            </DialogClose>
                            <Button
                                onClick={handleChange}
                                disabled={isLoading}>
                                {(isLoading) ? <UpdateIcon className='loader-spinner' /> : <>Lägg till</>}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

export default AddCertificationDialog;
